import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Target, Users, TrendingUp, Calendar, MapPin, CheckCircle, FileText, 
  BarChart3, Plus, Search, Phone, Download, Edit, Trash2, GitBranch, 
  Handshake, LandPlot, MessageCircle, Eye, Building, Award, Star,
  ArrowUpRight, ArrowDownRight, Filter, RefreshCw, Clock, XCircle, Pause,
  ChevronDown, ChevronRight, UserPlus, Upload, FileSpreadsheet, AlertCircle,
  Table, PieChart as PieChartIcon, LineChart, Activity, CheckSquare, Info,
  MessageSquare, History, Home, Loader2
} from 'lucide-react';
import CPStructureTab from './CPStructureTab';
import OverviewTab from './marketing/OverviewTab';
import TrendTab from './marketing/TrendTab';
import MarketingDMTab from './marketing/MarketingDMTab';
import ReportsTab from './marketing/ReportsTab';
import DMIntegrationTab from './marketing/DMIntegrationTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, LineChart as RechartsLineChart, Line } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lead Stages (8 stages as per requirement)
const LEAD_STAGES = [
  { id: 'new_lead', label: 'New Lead', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-100' },
  { id: 'contacted', label: 'Contacted', color: 'bg-cyan-500', textColor: 'text-cyan-700', bgLight: 'bg-cyan-100' },
  { id: 'office_visit_meeting', label: 'Office Visit / Meeting', color: 'bg-indigo-500', textColor: 'text-indigo-700', bgLight: 'bg-indigo-100' },
  { id: 'site_visit', label: 'Site Visit', color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-100' },
  { id: 'interested', label: 'Interested', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-100' },
  { id: 'cancelled', label: 'Cancelled', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-100' },
  { id: 'booking', label: 'Booking', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-100' },
  { id: 'documentation', label: 'Documentation', color: 'bg-teal-500', textColor: 'text-teal-700', bgLight: 'bg-teal-100' },
  { id: 'closed', label: 'Closed', color: 'bg-stone-500', textColor: 'text-stone-700', bgLight: 'bg-stone-100' },
];

// Phase Types for Dream Nagpur City
const NAGPUR_PHASE_TYPES = ['Phase A', 'Phase B', 'Phase C'];

// Remark Categories mapped by Status
const STATUS_REMARK_MAPPING = {
  INPROCESS: [
    "CALL BACK LATER",
    "OFFICE VISIT SCHEDULE",
    "OFFICE VISIT DONE",
    "PROJECT VISIT SCHEDULE",
    "PROJECT VISIT DONE",
    "ASK PROJECT DETAILS"
  ],
  HOLD: [
    "HIGH RATE",
    "INSTALLMENT",
    "LONG DISTANCE",
    "NA PLOT",
    "NO DEVELOPMENT",
    "NO MONEY"
  ],
  CANCEL: [
    "CP WORKING",
    "NOT INTERESTED"
  ],
  DONE: [
    "BOOKING",
    "BOOKING INSTALLMENTS",
    "KHAREDIKHAT INPROCESS",
    "KHAREDIKHAT DONE",
    "7/12 INPROCESS",
    "7/12 DONE",
    "POSSESSION"
  ]
};

// All Remark Categories (combined for filters)
const REMARK_CATEGORIES = [
  ...STATUS_REMARK_MAPPING.INPROCESS,
  ...STATUS_REMARK_MAPPING.HOLD,
  ...STATUS_REMARK_MAPPING.CANCEL,
  ...STATUS_REMARK_MAPPING.DONE
];

// Status Categories
const STATUS_CATEGORIES = ["INPROCESS", "HOLD", "CANCEL", "DONE"];

// Status Colors
const STATUS_COLORS = {
  INPROCESS: { bg: 'bg-yellow-100', text: 'text-yellow-800', color: '#eab308' },
  HOLD: { bg: 'bg-blue-100', text: 'text-blue-800', color: '#3b82f6' },
  CANCEL: { bg: 'bg-red-100', text: 'text-red-800', color: '#ef4444' },
  DONE: { bg: 'bg-green-100', text: 'text-green-800', color: '#10b981' }
};

// Reverse map: remark/particular → status category (for row coloring)
const REMARK_TO_STATUS = (() => {
  const m = {};
  Object.entries(STATUS_REMARK_MAPPING).forEach(([status, remarks]) => {
    remarks.forEach(r => { m[r.toUpperCase()] = status; });
  });
  // Common variants / typos seen in imported data
  m['NOT INTRESTED'] = 'CANCEL';
  m['INTERESTED'] = 'INPROCESS';
  return m;
})();

// Returns a Tailwind className for a row based on its remark/status category
const getRemarkRowClass = (particular, status) => {
  const norm = (particular || '').toString().trim().toUpperCase();
  const cat = status || REMARK_TO_STATUS[norm];
  switch (cat) {
    case 'INPROCESS': return 'bg-yellow-50 hover:bg-yellow-100';
    case 'HOLD': return 'bg-blue-50 hover:bg-blue-100';
    case 'CANCEL': return 'bg-red-50 hover:bg-red-100';
    case 'DONE': return 'bg-green-50 hover:bg-green-100';
    default: return 'bg-white hover:bg-stone-50';
  }
};

// Deal Types
const DEAL_TYPES = ['Normal', 'Barter', 'Normal + Barter'];

// Land Sizes (Gunthe)
const LAND_SIZES = ['2 R', '3 R', '4 R', '5 R', '8 R', '10 R', '11 R', '20 R', '40 R', '1 Acre', '2 Acre', '5 Acre', '10 Acre'];

// Configuration options (for flat/apartment projects)
const CONFIGURATION_OPTIONS = ['1RK', '1BHK', '2BHK', '3BHK', 'SHOP', 'Other'];

// Reference Source options
const REFERENCE_SOURCES = ['Agent', 'Company', 'Social Media', 'Reference'];

// Total Target
const TOTAL_LEAD_TARGET = 14298;

// Main Project Categories for DM Dashboard
const PROJECT_CATEGORIES = [
  { id: 'dream_kokan', name: 'Dream Kokan City', color: '#3b82f6', bgClass: 'bg-blue-500', bgLight: 'bg-blue-100', textColor: 'text-blue-800', type: 'land' },
  { id: 'dream_nagpur', name: 'Dream Nagpur City', color: '#f97316', bgClass: 'bg-orange-500', bgLight: 'bg-orange-100', textColor: 'text-orange-800', type: 'land' },
  { id: 'siddhivinayak_infra', name: 'Siddhivinayak Infra', color: '#10b981', bgClass: 'bg-emerald-500', bgLight: 'bg-emerald-100', textColor: 'text-emerald-800', type: 'flat', 
    subProjects: [
      { id: 'brahma_heights', name: 'Brahma Heights' },
      { id: 'godavari_heights', name: 'Godavari Heights' },
      { id: 'siddhivinayak_heights', name: 'Siddhivinayak Heights' },
      { id: 'kaveri_heights', name: 'Kaveri Heights' }
    ]
  },
  { id: 'dream_riviera_a', name: "Dream Riviera 'A' Wing", color: '#8b5cf6', bgClass: 'bg-violet-500', bgLight: 'bg-violet-100', textColor: 'text-violet-800', type: 'flat' },
  { id: 'dream_riviera_b', name: "Dream Riviera 'B' Wing", color: '#ec4899', bgClass: 'bg-pink-500', bgLight: 'bg-pink-100', textColor: 'text-pink-800', type: 'flat' },
  { id: 'sai_plaza', name: 'Sai Plaza', color: '#14b8a6', bgClass: 'bg-teal-500', bgLight: 'bg-teal-100', textColor: 'text-teal-800', type: 'flat' }
];

// All projects for dropdown (including Siddhivinayak sub-projects)
const ALL_PROJECTS_FOR_DROPDOWN = [
  { id: 'dream_kokan', name: 'Dream Kokan City', type: 'land' },
  { id: 'dream_nagpur', name: 'Dream Nagpur City', type: 'land' },
  { id: 'brahma_heights', name: 'Brahma Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'godavari_heights', name: 'Godavari Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'siddhivinayak_heights', name: 'Siddhivinayak Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'kaveri_heights', name: 'Kaveri Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'dream_riviera_a', name: "Dream Riviera 'A' Wing", type: 'flat' },
  { id: 'dream_riviera_b', name: "Dream Riviera 'B' Wing", type: 'flat' },
  { id: 'sai_plaza', name: 'Sai Plaza', type: 'flat' }
];

// INPROCESS Sub-Categories (for quick lead add in DM Dashboard)
const INPROCESS_CATEGORIES = [
  { id: 'call_back_later', name: 'Call Back Later', icon: Phone, color: 'bg-amber-500', lightBg: 'bg-amber-50', textColor: 'text-amber-700' },
  { id: 'office_visit_schedule', name: 'Office Visit Schedule', icon: Calendar, color: 'bg-blue-500', lightBg: 'bg-blue-50', textColor: 'text-blue-700' },
  { id: 'office_visit_done', name: 'Office Visit Done', icon: CheckCircle, color: 'bg-green-500', lightBg: 'bg-green-50', textColor: 'text-green-700' },
  { id: 'project_visit_schedule', name: 'Project Visit Schedule', icon: MapPin, color: 'bg-purple-500', lightBg: 'bg-purple-50', textColor: 'text-purple-700' },
  { id: 'project_visit_done', name: 'Project Visit Done', icon: CheckSquare, color: 'bg-teal-500', lightBg: 'bg-teal-50', textColor: 'text-teal-700' },
  { id: 'ask_project_details', name: 'Ask Project Details', icon: Info, color: 'bg-indigo-500', lightBg: 'bg-indigo-50', textColor: 'text-indigo-700' }
];

// Real DM Team Data with Reference IDs (as per master data)
const DM_TEAM_DATA = [
  { id: 'DM-001', refId: 'DM001', name: 'RASHMI ROHANKAR', region: 'Ratnagiri', leads: 5490, officeVisit: 2983, projectVisit: 1369, scope: 268, bookings: 219, 
    adms: [
      { id: 'ADM-006', refId: 'ADM006', name: 'SUNIL SHINGARE', leads: 95, bookings: 7 },
      { id: 'ADM-009', refId: 'ADM009', name: 'SNEHA KAMBLE', leads: 45, bookings: 4 },
    ]
  },
  { id: 'DM-002', refId: 'DM002', name: 'SOMNATH JADHAV', region: 'Pune', leads: 4614, officeVisit: 3400, projectVisit: 1530, scope: 287, bookings: 249,
    adms: [
      { id: 'ADM-001', refId: 'ADM001', name: 'RAMESH KALE', leads: 751, bookings: 15 },
      { id: 'ADM-002', refId: 'ADM002', name: 'ABBAS ANSARI', leads: 20, bookings: 9 },
      { id: 'ADM-003', refId: 'ADM003', name: 'NAVNATH MAGAR', leads: 172, bookings: 6 },
      { id: 'ADM-004', refId: 'ADM004', name: 'VIKAS WAGHMARE', leads: 85, bookings: 8 },
      { id: 'ADM-005', refId: 'ADM005', name: 'NARENDRA MUKUND', leads: 120, bookings: 10 },
    ]
  },
  { id: 'DM-003', refId: 'DM003', name: 'COMPANY', region: 'Corporate', leads: 3003, officeVisit: 2217, projectVisit: 932, scope: 118, bookings: 118, adms: [] },
  { id: 'DM-004', refId: 'DM004', name: 'SHIRISH NISAL', region: 'Mumbai', leads: 492, officeVisit: 490, projectVisit: 254, scope: 79, bookings: 77,
    adms: [
      { id: 'ADM-010', refId: 'ADM010', name: 'SANJAY TULASKAR', leads: 18, bookings: 5 },
      { id: 'ADM-011', refId: 'ADM011', name: 'DR. VISHAL SUPEKAR', leads: 12, bookings: 3 },
      { id: 'ADM-012', refId: 'ADM012', name: 'SANTOSH MAHALE', leads: 28, bookings: 2 },
    ]
  },
  { id: 'DM-005', refId: 'DM005', name: 'SANJAY GUND', region: 'Nagpur', leads: 244, officeVisit: 158, projectVisit: 65, scope: 12, bookings: 12,
    adms: [{ id: 'ADM-008', refId: 'ADM008', name: 'SANTOSH BAMNALE', leads: 17, bookings: 3 }]
  },
  { id: 'DM-006', refId: 'DM006', name: 'KIRAN THITE', region: 'Kolhapur', leads: 139, officeVisit: 112, projectVisit: 65, scope: 23, bookings: 19, adms: [] },
  { id: 'DM-007', refId: 'DM007', name: 'DATTA WADITAKE', region: 'Sangli', leads: 95, officeVisit: 57, projectVisit: 37, scope: 18, bookings: 18, adms: [] },
  { id: 'DM-008', refId: 'DM008', name: 'BABASAHEB BADE', region: 'Satara', leads: 90, officeVisit: 90, projectVisit: 41, scope: 8, bookings: 7, adms: [] },
  { id: 'DM-009', refId: 'DM009', name: 'PRAMOD CHAVAN', region: 'Nashik', leads: 79, officeVisit: 79, projectVisit: 45, scope: 4, bookings: 0, adms: [] },
  { id: 'DM-010', refId: 'DM010', name: 'TIPANNA CHALWADI', region: 'Solapur', leads: 40, officeVisit: 23, projectVisit: 9, scope: 4, bookings: 4, adms: [] },
  { id: 'DM-011', refId: 'DM011', name: 'VALMIK GHADHAVE', region: 'Ahmednagar', leads: 4, officeVisit: 4, projectVisit: 2, scope: 0, bookings: 0, adms: [] },
];

// CP/SCP Data with RCPs
const CP_DATA = [
  { id: 'CP-001', refId: 'CP001', name: 'BALASAHEB PAWAR', type: 'SCP', admRef: 'ADM001', dmRef: 'DM002', contact: '7350313140', leads: 45, bookings: 12,
    rcps: [
      { id: 'RCP-001', name: 'RAJESH PATIL', contact: '9876543210', leads: 15, bookings: 4 },
      { id: 'RCP-002', name: 'SURESH KULKARNI', contact: '9876543211', leads: 12, bookings: 3 },
    ]
  },
  { id: 'CP-002', refId: 'CP002', name: 'MANISHA GANGARDE', type: 'CP', admRef: 'ADM001', dmRef: 'DM002', contact: '7775923500', leads: 28, bookings: 8,
    rcps: [{ id: 'RCP-003', name: 'PRIYA SHARMA', contact: '9876543212', leads: 8, bookings: 2 }]
  },
  { id: 'CP-003', refId: 'CP003', name: 'VAISHALI', type: 'CP', admRef: 'ADM002', dmRef: 'DM002', contact: '9890818106', leads: 22, bookings: 6, rcps: [] },
  { id: 'CP-004', refId: 'CP004', name: 'RAMESH GHODKE', type: 'SCP', admRef: 'ADM005', dmRef: 'DM002', contact: '9881022443', leads: 35, bookings: 10,
    rcps: [
      { id: 'RCP-004', name: 'VIJAY DESHMUKH', contact: '9876543213', leads: 10, bookings: 3 },
      { id: 'RCP-005', name: 'ANIL JADHAV', contact: '9876543214', leads: 8, bookings: 2 },
    ]
  },
  { id: 'CP-005', refId: 'CP005', name: 'SANDEEP BANSODE', type: 'CP', admRef: 'ADM004', dmRef: 'DM002', contact: '9325112525', leads: 18, bookings: 5, rcps: [] },
];

const OVERVIEW_PROJECTS = [
  { id: 'all', label: 'All Projects', projectKeys: [] },
  { id: 'dream_kokan', label: 'Dream Kokan City', projectKeys: ['dream_kokan'] },
  { id: 'dream_nagpur', label: 'Dream Nagpur City', projectKeys: ['dream_nagpur'] },
  { id: 'siddhivinayak', label: 'Siddhivinayak Infra', projectKeys: ['brahma_heights', 'godavari_heights', 'siddhivinayak_heights', 'kaveri_heights'] },
  { id: 'brahma_heights', label: 'Brahma Heights', projectKeys: ['brahma_heights'], parent: 'siddhivinayak' },
  { id: 'godavari_heights', label: 'Godavari Heights', projectKeys: ['godavari_heights'], parent: 'siddhivinayak' },
  { id: 'siddhivinayak_heights', label: 'Siddhivinayak Heights', projectKeys: ['siddhivinayak_heights'], parent: 'siddhivinayak' },
  { id: 'kaveri_heights', label: 'Kaveri Heights', projectKeys: ['kaveri_heights'], parent: 'siddhivinayak' },
  { id: 'dream_riviera_a', label: 'Dream Riviera A Wing', projectKeys: ['dream_riviera_a'] },
  { id: 'dream_riviera_b', label: 'Dream Riviera B Wing', projectKeys: ['dream_riviera_b'] },
  { id: 'sai_plaza', label: 'Sai Plaza', projectKeys: ['sai_plaza'] }
];

const getOverviewDatePresets = () => {
  const today = new Date().toISOString().split('T')[0];
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 of current month = last day of prev month
  const fmt = (d) => d.toISOString().split('T')[0];
  return [
    { id: 'all_time', label: 'All Time', from: '', to: '' },
    { id: 'this_year', label: 'This Year', from: `${now.getFullYear()}-01-01`, to: today },
    { id: 'last_year', label: 'Last Year', from: `${now.getFullYear() - 1}-01-01`, to: `${now.getFullYear() - 1}-12-31` },
    { id: 'this_month', label: 'This Month', from: `${now.toISOString().slice(0, 7)}-01`, to: today },
    { id: 'last_month', label: 'Last Month', from: fmt(lastMonth), to: fmt(lastMonthEnd) },
    { id: 'last_3_months', label: 'Last 3 Months', from: (() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().split('T')[0]; })(), to: today },
    { id: 'last_6_months', label: 'Last 6 Months', from: (() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d.toISOString().split('T')[0]; })(), to: today },
  ];
};

// ==================== STAGE WISE TAB ====================
const STAGE_STATUS_STYLES = {
  DONE:      { bg: 'bg-green-100',  text: 'text-green-800',  label: 'DONE' },
  HOLD:      { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'HOLD' },
  Inprocess: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Inprocess' },
  NILL:      { bg: 'bg-slate-100',  text: 'text-slate-500',  label: 'NILL' },
};
const STAGE_STATUS_OPTIONS = ['DONE', 'HOLD', 'Inprocess', 'NILL'];

const StageWiseTab = () => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/marketing-dashboard/stage-report`);
        const data = await res.json();
        setRows(data.rows || []);
        setColumns(data.columns || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const startEdit = (row) => {
    setEditingRow(row.srNo);
    const vals = {};
    columns.forEach(c => { vals[c.key] = row[c.key] || 'DONE'; });
    setEditValues(vals);
  };

  const cancelEdit = () => { setEditingRow(null); setEditValues({}); };

  const saveEdit = async (srNo) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/marketing-dashboard/stage-report/${srNo}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues),
      });
      const updated = await res.json();
      setRows(prev => prev.map(r => r.srNo === srNo ? { ...r, ...updated } : r));
      setEditingRow(null);
      setEditValues({});
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/marketing-dashboard/stage-report/export`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'stage_report.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <Card className="border-0 shadow-md" data-testid="stage-wise-tab">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg font-bold text-slate-900">Marketing Department — All Site Stage Report</CardTitle>
            <p className="text-sm text-slate-500 mt-0.5">Status tracking across all projects and marketing activities</p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="stage-wise-export-btn"
            className="flex items-center gap-2 px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 flex-wrap pt-2">
          {STAGE_STATUS_OPTIONS.map(s => {
            const st = STAGE_STATUS_STYLES[s] || STAGE_STATUS_STYLES.NILL;
            return (
              <span key={s} className={`px-3 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{s}</span>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="stage-wise-table">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="px-3 py-3 text-center font-semibold border border-slate-700 whitespace-nowrap">SR. NO.</th>
                <th className="px-3 py-3 text-left font-semibold border border-slate-700 whitespace-nowrap min-w-[160px]">PARTICULAR</th>
                {columns.map(col => (
                  <th key={col.key} className="px-3 py-3 text-center font-semibold border border-slate-700 whitespace-nowrap min-w-[110px]">{col.label}</th>
                ))}
                <th className="px-3 py-3 text-center font-semibold border border-slate-700 whitespace-nowrap">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isEditing = editingRow === row.srNo;
                return (
                  <tr key={row.srNo} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-testid={`stage-row-${row.srNo}`}>
                    <td className="px-3 py-2.5 text-center font-semibold text-slate-600 border border-slate-200">{row.srNo}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-800 border border-slate-200">{row.particular}</td>
                    {columns.map(col => {
                      const val = isEditing ? (editValues[col.key] || 'DONE') : (row[col.key] || 'NILL');
                      const st = STAGE_STATUS_STYLES[val] || STAGE_STATUS_STYLES.NILL;
                      return (
                        <td key={col.key} className="px-2 py-2 text-center border border-slate-200">
                          {isEditing ? (
                            <select
                              value={editValues[col.key] || 'DONE'}
                              onChange={e => setEditValues(prev => ({ ...prev, [col.key]: e.target.value }))}
                              className={`text-xs px-1 py-1 rounded border border-slate-300 font-semibold ${st.bg} ${st.text} cursor-pointer`}
                              data-testid={`stage-select-${row.srNo}-${col.key}`}
                            >
                              {STAGE_STATUS_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${st.bg} ${st.text}`}>{val}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-center border border-slate-200">
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => saveEdit(row.srNo)}
                            disabled={saving}
                            data-testid={`stage-save-${row.srNo}`}
                            className="px-2.5 py-1 bg-green-700 hover:bg-green-800 text-white rounded text-[10px] font-bold disabled:opacity-60"
                          >
                            {saving ? '...' : 'Save'}
                          </button>
                          <button
                            onClick={cancelEdit}
                            data-testid={`stage-cancel-${row.srNo}`}
                            className="px-2.5 py-1 bg-slate-400 hover:bg-slate-500 text-white rounded text-[10px] font-bold"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(row)}
                          data-testid={`stage-edit-${row.srNo}`}
                          className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-bold flex items-center gap-1 mx-auto"
                        >
                          <Edit className="w-3 h-3" /> Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};


const MarketingDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = useRef(null);
  const [filterStage, setFilterStage] = useState('all');
  const [filterDM, setFilterDM] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRemark, setFilterRemark] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterGunthe, setFilterGunthe] = useState('all');
  const [filterConfiguration, setFilterConfiguration] = useState('all');
  const [distinctGuntheOptions, setDistinctGuntheOptions] = useState([]);
  const [distinctConfigOptions, setDistinctConfigOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const LEADS_PER_PAGE = 50;
  // Lead-table column sort
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };
  const [expandedDMs, setExpandedDMs] = useState({});
  const [expandedADMs, setExpandedADMs] = useState({});
  const [expandedCPs, setExpandedCPs] = useState({});
  
  // Lead Form Modal
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [leadForm, setLeadForm] = useState({
    firstName: '', middleName: '', lastName: '', phone: '', email: '', address: '',
    dmName: '', admName: '', cpName: '', cpRefId: '', gunthe: '', dealType: 'Normal',
    stage: 'new_lead', bookingDate: '', remark: '', remarkCategory: '', inprocessCategory: '',
    officeVisit: false, projectVisit: false, scopeOfCustomer: false,
    project: 'dream_kokan', status: 'INPROCESS',
    // New fields for flat projects
    referenceSource: '', referenceSourceManual: '', attendedBy: '', configuration: '',
    flatNo: '', shopNo: '',
    // Phase Type for Dream Nagpur City
    phaseType: '',
    // RCP Name and Employee Name (who added the lead)
    rcpName: '', addedByEmployee: '',
    // Conversation fields for edit
    newConversation: '', conversationEmployeeName: ''
  });

  // Import/Export state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProject, setImportProject] = useState('');  // Project override for import
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);

  // Stats from API
  const [stats, setStats] = useState({
    totalLeads: 0,
    officeVisits: 0,
    projectVisits: 0,
    scopeCustomers: 0,
    bookings: 0,
    confirmSale: 0,
    registry: 0,
    possession: 0,
    inProcess: 0,
    cancelled: 0,
    onHold: 0,
    done: 0,
    completionPercent: 0,
    conversionRate: 0,
  });

  // Lead-wise Remark Data
  const [remarkData, setRemarkData] = useState([]);
  const [remarkGrandTotal, setRemarkGrandTotal] = useState(null);

  // Stage Bifurcation Data
  const [stageBifurcation, setStageBifurcation] = useState([]);
  const [stageGrandTotal, setStageGrandTotal] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});

  // DM Performance Data
  const [dmPerformance, setDmPerformance] = useState([]);
  const [dmGrandTotal, setDmGrandTotal] = useState(null);

  // Year/Month Trend Data
  const [yearMonthTrend, setYearMonthTrend] = useState([]);
  const [expandedYears, setExpandedYears] = useState({});
  const [selectedTrendProject, setSelectedTrendProject] = useState('all');
  const [trendYear, setTrendYear] = useState(null);
  // Day-wise drilldown state for Trend table: keys are `${year}-${monthNum}`
  const [expandedMonths, setExpandedMonths] = useState({});
  const [monthDayData, setMonthDayData] = useState({});
  const [projectTrendData, setProjectTrendData] = useState({});

  // Project Dashboard State
  const [selectedProject, setSelectedProject] = useState('all_flat');
  const [projectDmPerformance, setProjectDmPerformance] = useState([]);
  const [projectDmGrandTotal, setProjectDmGrandTotal] = useState(null);
  const [projectSummary, setProjectSummary] = useState([]);
  const [projectGrandTotal, setProjectGrandTotal] = useState(null);
  const [agentWiseData, setAgentWiseData] = useState([]);
  const [requirementWiseData, setRequirementWiseData] = useState([]);
  const [referenceWiseData, setReferenceWiseData] = useState([]);
  // All-projects specific data (for DM Wise, Gunthe Wise, Address Wise tabs)
  const [allDmData, setAllDmData] = useState([]);
  const [allDmGrandTotal, setAllDmGrandTotal] = useState({});
  const [allGuntheData, setAllGuntheData] = useState([]);
  const [allAddressData, setAllAddressData] = useState([]);
  const [addressWiseData, setAddressWiseData] = useState([]);
  const [dmDashboardView, setDmDashboardView] = useState('agent'); // 'agent', 'requirement', 'reference'

  // Main Index Data
  const [mainIndexData, setMainIndexData] = useState([]);
  const [mainIndexTotal, setMainIndexTotal] = useState(null);
  const [mainIndexLoading, setMainIndexLoading] = useState(false);
  const [overviewProject, setOverviewProject] = useState('all');
  // Bumps each time a lead is created / edited / deleted so child tabs (Reports, Trend, etc.) refresh.
  const [leadsRefreshKey, setLeadsRefreshKey] = useState(0);

  // Live clock for the header (updates every second)
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const [overviewStats, setOverviewStats] = useState({ totalLeads: 0, statusCounts: {}, remarkCounts: {}, stageCounts: {} });
  const [overviewDateFrom, setOverviewDateFrom] = useState('');
  const [overviewDateTo, setOverviewDateTo] = useState('');
  const [todayStats, setTodayStats] = useState({ leads: 0, officeVisit: 0, projectVisit: 0, scope: 0, bookings: 0, confirmSale: 0 });

  // Quick Add Lead Modal State (for DM Dashboard)
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [quickAddCategory, setQuickAddCategory] = useState(null);
  const [quickAddForm, setQuickAddForm] = useState({
    firstName: '', middleName: '', lastName: '', phone: '',
    gunthe: '', project: 'dream_kokan', dmName: '',
    initialConversation: '', employeeName: ''
  });

  // Conversation History Modal State
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [conversationLead, setConversationLead] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [newConversation, setNewConversation] = useState({ remark: '', employeeName: '', category: '' });

  // Fetch leads from API with server-side pagination & filters
  const fetchLeads = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', LEADS_PER_PAGE);
      params.set('skip', (page - 1) * LEADS_PER_PAGE);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterProject !== 'all') params.set('project', filterProject);
      if (filterStage !== 'all') params.set('stage', filterStage);
      if (filterDM !== 'all') params.set('dm_name', filterDM);
      if (filterRemark !== 'all') params.set('remark', filterRemark);
      if (filterGunthe !== 'all') params.set('gunthe', filterGunthe);
      if (filterConfiguration !== 'all') params.set('configuration', filterConfiguration);
      if (searchTerm) params.set('search', searchTerm);
      if (sortField) {
        params.set('sortField', sortField);
        params.set('sortOrder', sortOrder);
      }
      const response = await fetch(`${API}/marketing-dashboard/leads?${params.toString()}`);
      const data = await response.json();
      if (data.leads) {
        setLeads(data.leads);
        setTotalLeads(data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats from API
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats`);
      const data = await response.json();
      if (data) {
        const bookings = data.bookings || 0;
        const totalLeads = data.totalLeads || 0;
        setStats({
          totalLeads: totalLeads,
          officeVisits: data.officeVisits || 0,
          projectVisits: data.projectVisits || 0,
          scopeCustomers: data.scopeCustomers || 0,
          bookings: bookings,
          confirmSale: data.confirmSale || 0,
          registry: data.registry || 0,
          possession: data.possession || 0,
          inProcess: data.inProcess || 0,
          done: data.done || 0,
          cancelled: data.cancelled || 0,
          onHold: data.onHold || 0,
          completionPercent: totalLeads > 0 ? ((bookings / TOTAL_LEAD_TARGET) * 100).toFixed(2) : 0,
          conversionRate: totalLeads > 0 ? ((bookings / totalLeads) * 100).toFixed(2) : 0,
        });
      }
    } catch (error) {
    }
  };

  // Fetch Lead-wise Remark Data
  const fetchRemarkData = async (project = 'all') => {
    try {
      const query = project && project !== 'all' ? `?project=${project}` : '';
      const response = await fetch(`${API}/marketing-dashboard/stats/lead-wise-remark${query}`);
      const data = await response.json();
      if (data) {
        setRemarkData(data.remarkData || []);
        setRemarkGrandTotal(data.grandTotal || null);
      }
    } catch (error) {
    }
  };

  // Fetch Stage Bifurcation
  const fetchStageBifurcation = async () => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/stage-bifurcation`);
      const data = await response.json();
      if (data) {
        setStageBifurcation(data.stageBifurcation || []);
        setStatusCounts(data.statusCounts || {});
        
        // Calculate Grand Total for Stage Bifurcation
        if (data.stageBifurcation && data.stageBifurcation.length > 0) {
          const grandTotal = data.stageBifurcation.reduce((acc, row) => ({
            onlyLeadDone: (acc.onlyLeadDone || 0) + (row.onlyLeadDone || 0),
            onlyOfficeVisitDone: (acc.onlyOfficeVisitDone || 0) + (row.onlyOfficeVisitDone || 0),
            onlyProjectVisitDone: (acc.onlyProjectVisitDone || 0) + (row.onlyProjectVisitDone || 0),
            onlyScopeDone: (acc.onlyScopeDone || 0) + (row.onlyScopeDone || 0),
            onlyBookingDone: (acc.onlyBookingDone || 0) + (row.onlyBookingDone || 0)
          }), {});
          setStageGrandTotal(grandTotal);
        }
      }
    } catch (error) {
    }
  };

  // Fetch DM Performance
  const fetchDmPerformance = async () => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/dm-performance`);
      const data = await response.json();
      if (data) {
        setDmPerformance(data.dmPerformance || []);
        setDmGrandTotal(data.grandTotal || null);
      }
    } catch (error) {
    }
  };

  // Fetch Year/Month Trend
  const fetchYearMonthTrend = async (project = 'all') => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/year-month-trend?project=${project}`);
      const data = await response.json();
      if (data) {
        setYearMonthTrend(data.yearMonthTrend || []);
      }
    } catch (error) {
    }
  };

  // Fetch Project Summary
  const fetchProjectSummary = async () => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/project-summary`);
      const data = await response.json();
      if (data) {
        setProjectSummary(data.projectSummary || []);
        setProjectGrandTotal(data.grandTotal || null);
      }
    } catch (error) {
    }
  };

  // Fetch Project-specific DM Performance
  const fetchProjectDmPerformance = async (project) => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/dm-performance/${project}`);
      const data = await response.json();
      if (data) {
        setProjectDmPerformance(data.dmPerformance || []);
        setProjectDmGrandTotal(data.grandTotal || null);
      }
    } catch (error) {
    }
  };

  // Fetch Agent-wise Data
  const fetchAgentWiseData = async (project) => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/agent-wise/${project}`);
      const data = await response.json();
      if (data) {
        setAgentWiseData(data.agentData || []);
      }
    } catch (error) {
    }
  };

  // Fetch Requirement-wise Data
  const fetchRequirementWiseData = async (project) => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/requirement-wise/${project}`);
      const data = await response.json();
      if (data) {
        setRequirementWiseData(data.requirementData || []);
      }
    } catch (error) {
    }
  };

  // Fetch Reference-wise Data
  const fetchReferenceWiseData = async (project) => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/reference-wise/${project}`);
      const data = await response.json();
      if (data) {
        setReferenceWiseData(data.referenceData || []);
      }
    } catch (error) {
    }
  };

  // Fetch Address-wise Data (for land projects: Dream Kokan / Dream Nagpur)
  const fetchAddressWiseData = async (project) => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/stats/address-wise/${project}`);
      const data = await response.json();
      if (data) {
        setAddressWiseData(data.addressData || []);
      }
    } catch (error) {
    }
  };

  // Fetch All-Projects DM Wise data
  const fetchAllDmData = async () => {
    try {
      const res = await fetch(`${API}/marketing-dashboard/stats/agent-wise/all`);
      const data = await res.json();
      if (data) { setAllDmData(data.agentData || []); setAllDmGrandTotal(data.grandTotal || {}); }
    } catch (e) { console.error('Error fetching all-dm data:', e); }
  };

  // Fetch All-Projects Gunthe Wise data (land projects combined)
  const fetchAllGuntheData = async () => {
    try {
      const res = await fetch(`${API}/marketing-dashboard/stats/requirement-wise/all_land`);
      const data = await res.json();
      if (data) setAllGuntheData(data.requirementData || []);
    } catch (e) { console.error('Error fetching all-gunthe data:', e); }
  };

  // Fetch All-Projects Address Wise data
  const fetchAllAddressData = async () => {
    try {
      const res = await fetch(`${API}/marketing-dashboard/stats/address-wise/all`);
      const data = await res.json();
      if (data) setAllAddressData(data.addressData || []);
    } catch (e) { console.error('Error fetching all-address data:', e); }
  };

  // Fetch Main Index Data
  const fetchMainIndex = async () => {
    try {
      setMainIndexLoading(true);
      const response = await fetch(`${API}/marketing-dashboard/stats/main-index`);
      const data = await response.json();
      if (data) {
        setMainIndexData(data.mainIndex || []);
        setMainIndexTotal(data.total || null);
      }
    } catch (error) {
    } finally {
      setMainIndexLoading(false);
    }
  };

  // Fetch Overview Stats (full dataset, not paginated)
  const fetchOverviewStats = async (project = 'all', dateFrom = '', dateTo = '') => {
    try {
      let url = `${API}/marketing-dashboard/stats/overview?project=${project}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data) setOverviewStats(data);
    } catch (error) {
    }
  };

  const fetchTodayStats = async (project = 'all') => {
    try {
      const res = await fetch(`${API}/marketing-dashboard/stats/today?project=${project}`);
      const data = await res.json();
      if (data) setTodayStats(data);
    } catch (e) {
    }
  };

  // Initial load
  useEffect(() => {
    const initDashboard = async () => {
      await Promise.all([
        fetchStats(),
        fetchRemarkData(),
        fetchStageBifurcation(),
        fetchDmPerformance(),
        fetchYearMonthTrend(),
        fetchProjectSummary(),
        fetchMainIndex(),
        fetchOverviewStats(),
        fetchTodayStats(),
        fetchProjectDmPerformance('all_flat'),
        fetchAgentWiseData('all_flat'),
        fetchRequirementWiseData('all_flat'),
        fetchReferenceWiseData('all_flat'),
        fetchAllDmData(),
        fetchAllGuntheData(),
        fetchAllAddressData(),
        (async () => {
          try {
            const res = await fetch(`${API}/marketing-dashboard/distinct-filters`);
            const d = await res.json();
            setDistinctGuntheOptions(d.gunthe || []);
            setDistinctConfigOptions(d.configuration || []);
          } catch (e) { console.error('Error fetching distinct filters:', e); }
        })()
      ]);
    };
    initDashboard();
  }, []);

  // Fetch data when project changes
  useEffect(() => {
    const fetchProjectData = async () => {
      const isLandProject = selectedProject === 'dream_kokan' || selectedProject === 'dream_nagpur';
      const tasks = [
        fetchProjectDmPerformance(selectedProject),
        fetchRequirementWiseData(selectedProject),
      ];
      if (isLandProject) {
        tasks.push(fetchAddressWiseData(selectedProject));
      } else {
        tasks.push(fetchAgentWiseData(selectedProject));
        tasks.push(fetchReferenceWiseData(selectedProject));
      }
      await Promise.all(tasks);
    };
    fetchProjectData();
  }, [selectedProject]);

  // Re-fetch leads when filters or page change
  useEffect(() => {
    fetchLeads(currentPage);
  }, [currentPage, filterStatus, filterProject, filterStage, filterDM, filterRemark, filterGunthe, filterConfiguration, searchTerm, sortField, sortOrder]);

  // Auto-select latest year for Monthly Trend chart when data refreshes
  useEffect(() => {
    if (yearMonthTrend.length === 0) return;
    const years = yearMonthTrend.map(y => y.year);
    if (!trendYear || !years.includes(trendYear)) {
      setTrendYear(Math.max(...years));
    }
  }, [yearMonthTrend, trendYear]);

  // Computed stats for display
  const displayStats = {
    totalLeads: stats.totalLeads || 0,
    officeVisits: stats.officeVisits || 0,
    projectVisits: stats.projectVisits || 0,
    scopeOfCustomer: stats.scopeCustomers || 0,
    bookings: stats.bookings || 0,
    confirmSale: stats.confirmSale || 0,
    registry: stats.registry || 0,
    possession: stats.possession || 0,
    inProcess: stats.inProcess || 0,
    done: stats.done || 0,
    cancelled: stats.cancelled || 0,
    onHold: stats.onHold || 0,
    completionPercent: stats.completionPercent || '0',
    conversionRate: stats.conversionRate || 0,
  };

  // Stage-wise counts
  const stageWiseCounts = LEAD_STAGES.map(stage => ({
    ...stage,
    count: leads.filter(l => l.stage === stage.id).length
  }));

  // Status pie chart data
  const statusPieData = [
    { name: 'INPROCESS', value: stats.inProcess || statusCounts.INPROCESS || 0, color: STATUS_COLORS.INPROCESS.color },
    { name: 'DONE', value: stats.done || statusCounts.DONE || 0, color: STATUS_COLORS.DONE.color },
    { name: 'CANCEL', value: stats.cancelled || statusCounts.CANCEL || 0, color: STATUS_COLORS.CANCEL.color },
    { name: 'HOLD', value: stats.onHold || statusCounts.HOLD || 0, color: STATUS_COLORS.HOLD.color },
  ];

  // Open Lead Form
  const openLeadModal = (lead = null) => {
    if (lead) {
      setEditingLead(lead);
      setLeadForm({ 
        ...lead,
        newConversation: '',
        conversationEmployeeName: ''
      });
    } else {
      setEditingLead(null);
      setLeadForm({
        firstName: '', middleName: '', lastName: '', phone: '', email: '', address: '',
        dmName: '', admName: '', cpName: '', cpRefId: '', gunthe: '', dealType: 'Cash',
        stage: 'new_lead', bookingDate: '', remark: '', remarkCategory: '', inprocessCategory: '',
        officeVisit: false, projectVisit: false, scopeOfCustomer: false,
        project: 'dream_kokan', status: 'INPROCESS',
        referenceSource: '', referenceSourceManual: '', attendedBy: '', configuration: '',
        flatNo: '', shopNo: '',
        rcpName: '', addedByEmployee: '',
        newConversation: '', conversationEmployeeName: ''
      });
    }
    setIsLeadModalOpen(true);
  };

  // Open Quick Add Lead Modal (from DM Dashboard)
  const openQuickAddModal = (category) => {
    setQuickAddCategory(category);
    setQuickAddForm({
      firstName: '', middleName: '', lastName: '', phone: '',
      gunthe: '', project: selectedProject, dmName: '',
      initialConversation: '', employeeName: user?.name || ''
    });
    setIsQuickAddOpen(true);
  };

  // Handle Quick Add Lead Submit
  const handleQuickAddSubmit = async (e) => {
    e.preventDefault();
    if (!quickAddForm.middleName.trim()) {
      toast.error('Middle Name is mandatory!');
      return;
    }
    
    // Check if it's a land project - only validate gunthe for land projects
    const isLandProject = quickAddForm.project === 'dream_kokan' || quickAddForm.project === 'dream_nagpur';
    if (isLandProject && !quickAddForm.gunthe) {
      toast.error('Land Requirement (Gunthe) is mandatory for land projects!');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...quickAddForm,
        dealType: 'Cash',
        stage: 'new_lead',
        status: 'INPROCESS',
        inprocessCategory: quickAddCategory?.id,
        remarkCategory: quickAddCategory?.name?.toUpperCase() || 'CALL BACK LATER'
      };
      
      const response = await fetch(`${API}/marketing-dashboard/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success(`Lead added successfully with ${quickAddCategory?.name} status!`);
        setIsQuickAddOpen(false);
        await Promise.all([fetchLeads(), fetchStats(), fetchProjectSummary(), fetchMainIndex(), fetchProjectDmPerformance(selectedProject)]);
        fetchYearMonthTrend(selectedTrendProject);
        setLeadsRefreshKey((k) => k + 1);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create lead');
      }
    } catch (error) {
      toast.error('Error creating lead');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Conversation History Modal
  const openConversationModal = async (lead) => {
    setConversationLead(lead);
    setConversations(lead.conversations || []);
    setNewConversation({ remark: '', employeeName: user?.name || '', category: '' });
    setIsConversationModalOpen(true);
    
    // Fetch fresh conversation data
    try {
      const response = await fetch(`${API}/marketing-dashboard/leads/${lead.id || lead.leadId}/conversations`);
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
    }
  };

  // Add New Conversation
  const handleAddConversation = async () => {
    if (!newConversation.remark.trim()) {
      toast.error('Please enter a remark');
      return;
    }
    if (!newConversation.employeeName.trim()) {
      toast.error('Please enter employee name');
      return;
    }

    try {
      const response = await fetch(`${API}/marketing-dashboard/leads/${conversationLead.id || conversationLead.leadId}/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newConversation),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Conversation added successfully!');
        setConversations(prev => [...prev, data.conversation]);
        setNewConversation({ remark: '', employeeName: user?.name || '', category: '' });
        // Update the lead in the list
        setLeads(prev => prev.map(l => 
          (l.id === conversationLead.id || l.leadId === conversationLead.leadId) 
            ? { ...l, conversations: [...(l.conversations || []), data.conversation], lastConversation: data.conversation }
            : l
        ));
      } else {
        toast.error('Failed to add conversation');
      }
    } catch (error) {
      toast.error('Error adding conversation');
    }
  };

  // Handle Lead Submit
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.middleName.trim()) {
      toast.error('Middle Name is mandatory!');
      return;
    }
    
    // Check project type and validate accordingly
    const isLandProject = leadForm.project === 'dream_kokan' || leadForm.project === 'dream_nagpur';
    
    if (isLandProject) {
      if (!leadForm.gunthe) {
        toast.error('Land Requirement (Gunthe) is mandatory!');
        return;
      }
    }
    // Flat project validation removed - Reference Source and Configuration are now optional

    setSubmitting(true);
    try {
      if (editingLead) {
        const response = await fetch(`${API}/marketing-dashboard/leads/${editingLead.id || editingLead.leadId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadForm)
        });
        const data = await response.json();
        if (data.success) {
          toast.success('Lead updated successfully!');
          await Promise.all([fetchLeads(), fetchStats(), fetchMainIndex()]);
          fetchOverviewStats(selectedProject);
          fetchProjectSummary();
          fetchProjectDmPerformance(selectedProject);
          fetchYearMonthTrend(selectedTrendProject);
          setLeadsRefreshKey((k) => k + 1);
        } else {
          toast.error(data.detail || 'Failed to update lead');
        }
      } else {
        const response = await fetch(`${API}/marketing-dashboard/leads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadForm)
        });
        const data = await response.json();
        if (data.success) {
          toast.success('Lead created successfully!');
          await Promise.all([fetchLeads(), fetchStats(), fetchMainIndex()]);
          fetchOverviewStats(selectedProject);
          fetchProjectSummary();
          fetchProjectDmPerformance(selectedProject);
          fetchYearMonthTrend(selectedTrendProject);
          setLeadsRefreshKey((k) => k + 1);
        } else {
          toast.error(data.detail || 'Failed to create lead');
        }
      }
      setIsLeadModalOpen(false);
    } catch (error) {
      toast.error('Failed to save lead');
    } finally {
      setSubmitting(false);
    }
  };

  // Export leads to Excel
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const response = await fetch(`${API}/marketing-dashboard/export/excel`);
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `marketing_leads_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Leads exported successfully!');
    } catch (error) {
      toast.error('Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API}/marketing-dashboard/export/template`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'marketing_leads_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Handle file selection for import
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }
    
    setImportFile(file);
    setImportPreview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API}/marketing-dashboard/import/preview`, { method: 'POST', body: formData });
      const data = await response.json();
      setImportPreview(data);
      if (!data.success && data.error) toast.error(data.error);
    } catch (error) {
      toast.error('Failed to preview file');
    }
  };

  // Import leads from file
  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file first');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);
      if (importProject) formData.append('project_override', importProject);
      const response = await fetch(`${API}/marketing-dashboard/import`, { method: 'POST', body: formData });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${data.imported} leads imported successfully!`);
        if (data.skipped > 0) toast.warning(`${data.skipped} rows skipped due to errors`);
        setIsImportModalOpen(false);
        setImportFile(null);
        setImportProject('');
        setImportPreview(null);
        await fetchLeads();
        await fetchStats();
        fetchMainIndex();
        fetchYearMonthTrend(selectedTrendProject);
        setLeadsRefreshKey((k) => k + 1);
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Failed to import leads');
    } finally {
      setImporting(false);
    }
  };

  // Import historical data
  const handleHistoricalImport = async () => {
    if (!importFile) {
      toast.error('Please select a file first');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);
      if (importProject) formData.append('project_override', importProject);
      toast.info('Importing historical data... This may take a few minutes.');
      const response = await fetch(`${API}/marketing-dashboard/import-historical`, { method: 'POST', body: formData });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${data.imported.toLocaleString()} leads imported successfully!`);
        if (data.skipped > 0) toast.warning(`${data.skipped} rows skipped due to errors`);
        setIsImportModalOpen(false);
        setImportFile(null);
        setImportProject('');
        setImportPreview(null);
        await fetchLeads();
        await fetchStats();
        fetchMainIndex();
        fetchYearMonthTrend(selectedTrendProject);
        setLeadsRefreshKey((k) => k + 1);
      } else {
        toast.error(data.detail || 'Historical import failed');
      }
    } catch (error) {
      toast.error('Failed to import historical data');
    } finally {
      setImporting(false);
    }
  };

  // Leads are now server-side filtered, use them directly
  const filteredLeads = leads;

  // Toggle expansions
  const toggleDM = (dmId) => setExpandedDMs(prev => ({ ...prev, [dmId]: !prev[dmId] }));
  const toggleADM = (admId) => setExpandedADMs(prev => ({ ...prev, [admId]: !prev[admId] }));
  const toggleCP = (cpId) => setExpandedCPs(prev => ({ ...prev, [cpId]: !prev[cpId] }));
  const toggleYear = (year) => setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));

  // Toggle month expansion in Trend table; lazy-loads day-wise data on first expand
  const toggleMonthExpand = async (year, monthNum) => {
    const key = `${year}-${monthNum}`;
    const willOpen = !expandedMonths[key];
    setExpandedMonths(prev => ({ ...prev, [key]: willOpen }));
    if (willOpen && monthDayData[key] === undefined) {
      try {
        const projectParam = selectedTrendProject || 'all';
        const res = await fetch(`${API}/marketing-dashboard/stats/daily-trend?year=${year}&month=${monthNum}&project=${projectParam}`);
        const data = await res.json();
        setMonthDayData(prev => ({ ...prev, [key]: data.days || [] }));
      } catch (err) {
        setMonthDayData(prev => ({ ...prev, [key]: [] }));
      }
    }
  };

  // DM Chart Data
  const dmChartData = dmPerformance.length > 0 
    ? dmPerformance.slice(0, 10).map(dm => ({
        name: dm.dmName?.split(' ')[0] || 'Unknown',
        totalLead: dm.totalLead,
        totalOfficeVisit: dm.totalOfficeVisit,
        totalProjectVisit: dm.totalProjectVisit,
        totalScopeOfCustomer: dm.totalScopeOfCustomer,
        totalBooking: dm.totalBooking
      }))
    : DM_TEAM_DATA.slice(0, 10).map(dm => ({
        name: dm.name.split(' ')[0],
        totalLead: dm.leads,
        totalOfficeVisit: dm.officeVisit,
        totalProjectVisit: dm.projectVisit,
        totalScopeOfCustomer: dm.scope,
        totalBooking: dm.bookings
      }));

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="marketing-dashboard">
      {/* Modern Header with Glass Effect */}
      <div className="sticky top-0 z-30 glass-header">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Marketing Dashboard
              </h1>
              <p className="text-sm text-slate-500 mt-0.5" data-testid="dashboard-subtitle">Manage leads, track performance & analyze trends</p>
              <p className="text-xs text-slate-600 mt-1 flex items-center gap-2" data-testid="dashboard-datetime">
                <Calendar className="w-3.5 h-3.5 text-blue-500" />
                <span className="font-semibold text-slate-700">{now.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</span>
                <span className="text-slate-400">|</span>
                <Clock className="w-3.5 h-3.5 text-purple-500" />
                <span className="font-mono font-semibold text-slate-700 tabular-nums">{now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</span>
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Select
                value={overviewProject}
                onValueChange={(val) => {
                  setOverviewProject(val);
                  fetchOverviewStats(val, overviewDateFrom, overviewDateTo);
                  fetchTodayStats(val);
                  // Sync global filter across all tabs
                  setFilterProject(val);
                  setCurrentPage(1);
                  // Sync Remark & Bifurcation tab
                  fetchRemarkData(val);
                  // Sync Trend tab
                  setSelectedTrendProject(val);
                  fetchYearMonthTrend(val);
                  // Clear day-wise drilldown cache (data is now stale for new project)
                  setExpandedMonths({});
                  setMonthDayData({});
                  // Sync DM Dashboard's selected project (excludes Dream Kokan & Dream Nagpur from agent/req/ref views)
                  if (val === 'all') {
                    setSelectedProject('all_flat');
                    setDmDashboardView('agent');
                  } else if (val === 'siddhivinayak') {
                    setSelectedProject('siddhivinayak');
                    setDmDashboardView('agent_flat');
                  } else if (val === 'dream_kokan' || val === 'dream_nagpur') {
                    setSelectedProject(val);
                    setDmDashboardView('agent');
                  } else {
                    setSelectedProject(val);
                    setDmDashboardView('agent_flat');
                  }
                }}
              >
                <SelectTrigger className="w-52 h-9 border-slate-200 bg-white" data-testid="overview-project-dropdown">
                  <SelectValue placeholder="Select Project" />
                </SelectTrigger>
                <SelectContent>
                  {OVERVIEW_PROJECTS.map(p => (
                    <SelectItem
                      key={p.id}
                      value={p.id}
                      data-testid={`overview-project-option-${p.id}`}
                      className={p.parent ? 'pl-7 text-emerald-700' : ''}
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={(() => {
                  const match = getOverviewDatePresets().find(pr => pr.from === overviewDateFrom && pr.to === overviewDateTo);
                  return match ? match.id : 'custom';
                })()}
                onValueChange={(val) => {
                  const preset = getOverviewDatePresets().find(p => p.id === val);
                  if (preset) {
                    setOverviewDateFrom(preset.from);
                    setOverviewDateTo(preset.to);
                    fetchOverviewStats(overviewProject, preset.from, preset.to);
                  }
                }}
              >
                <SelectTrigger className="w-44 h-9 border-slate-200 bg-white" data-testid="overview-date-range-dropdown">
                  <Calendar className="w-4 h-4 mr-1 text-slate-500" />
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  {getOverviewDatePresets().map(p => (
                    <SelectItem key={p.id} value={p.id} data-testid={`overview-date-option-${p.id}`}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { fetchLeads(); fetchStats(); fetchMainIndex(); }}
                className="border-slate-200 hover:bg-slate-50 text-slate-700"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Main Tabs - Scrollable pill strip */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="bg-white border border-slate-200 rounded-xl p-1.5 mb-6 overflow-x-auto">
            <TabsList className="flex w-max min-w-full bg-transparent gap-1">
              <TabsTrigger value="overview"          className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-blue-600    data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="main-index"        className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-indigo-600  data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-main-index">Main Index</TabsTrigger>
              <TabsTrigger value="stage-wise"        className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-green-700   data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-stage-wise">Stage Wise</TabsTrigger>
              <TabsTrigger value="leads"             className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-slate-800   data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-leads">Lead Entry</TabsTrigger>
              <TabsTrigger value="remark-bifurcation" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-amber-600  data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-remark">Remark &amp; Bifurcation</TabsTrigger>
              <TabsTrigger value="dm-dashboard"      className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-dm">DM Dashboard</TabsTrigger>
              <TabsTrigger value="trend"             className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-violet-600  data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-trend">Trend</TabsTrigger>
              <TabsTrigger value="hierarchy"         className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-rose-600    data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-hierarchy">Hierarchy</TabsTrigger>
              <TabsTrigger value="cp-structure"      className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-pink-700    data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-cp-structure">CP Structure</TabsTrigger>
              <TabsTrigger value="dm-integration"    className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-sky-600     data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-dm-integration">DM Integration</TabsTrigger>
              <TabsTrigger value="reports"           className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-teal-600    data-[state=active]:text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-all" data-testid="tab-reports">Reports</TabsTrigger>
            </TabsList>
          </div>

        {/* ── Global Import / Export toolbar — visible on ALL tabs ── */}
        <div className="flex items-center gap-2 justify-end mb-4 px-1" data-testid="global-excel-toolbar">
          <span className="text-xs text-slate-400 mr-auto font-medium">
            {activeTab === 'leads' ? 'Lead data' : activeTab === 'overview' ? 'Overview data' : activeTab === 'main-index' ? 'Main index data' : activeTab === 'stage-wise' ? 'Stage wise data' : activeTab === 'remark-bifurcation' ? 'Remark data' : activeTab === 'dm-dashboard' ? 'DM data' : activeTab === 'trend' ? 'Trend data' : activeTab === 'hierarchy' ? 'Hierarchy data' : activeTab === 'cp-structure' ? 'CP structure data' : activeTab === 'dm-integration' ? 'DM integration data' : 'Reports data'} — Import or Export Excel
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsImportModalOpen(true)}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="global-import-excel-btn"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Import Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={exporting}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="global-export-excel-btn"
          >
            {exporting ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Export Excel
          </Button>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <OverviewTab overviewProject={overviewProject} overviewStats={overviewStats} />
        </TabsContent>

        {/* Main Index Tab */}
        <TabsContent value="main-index">
          <Card data-testid="main-index-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                MAIN INDEX
              </CardTitle>
            </CardHeader>
            <CardContent>
              {mainIndexLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
                  <span className="text-slate-500">Loading Main Index...</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse" data-testid="main-index-table">
                    <thead>
                      <tr className="bg-slate-900 text-white">
                        <th className="px-4 py-3 text-left border border-slate-700 font-semibold w-[220px]">PROJECT</th>
                        <th className="px-4 py-3 text-left border border-slate-700 font-semibold w-[160px]">PARTICULAR</th>
                        <th className="px-4 py-3 text-right border border-slate-700 font-semibold w-[120px]">REQ.</th>
                        <th className="px-4 py-3 text-right border border-slate-700 font-semibold w-[120px]">DONE</th>
                        <th className="px-4 py-3 text-right border border-slate-700 font-semibold w-[120px]">BAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Client-side filter for global project selector
                        const PROJECT_TO_MAIN_INDEX = {
                          'dream_kokan': 'KOKAN CITY',
                          'dream_nagpur': 'NAGPUR CITY',
                          'siddhivinayak': 'SIDDHIVINAYAK INFRA',
                          'brahma_heights': 'SIDDHIVINAYAK INFRA',
                          'godavari_heights': 'SIDDHIVINAYAK INFRA',
                          'siddhivinayak_heights': 'SIDDHIVINAYAK INFRA',
                          'kaveri_heights': 'SIDDHIVINAYAK INFRA',
                          'dream_riviera_a': 'DREAMS RIVIERA A-WING',
                          'dream_riviera_b': 'DREAMS RIVIERA B-WING',
                          'sai_plaza': 'SAI PLAZA',
                        };
                        const targetName = PROJECT_TO_MAIN_INDEX[overviewProject];
                        const filteredData = (overviewProject === 'all' || !targetName)
                          ? mainIndexData
                          : mainIndexData.filter(g => g.project === targetName);
                        return filteredData.map((group, gi) => {
                          const projectColors = ['bg-blue-50', 'bg-emerald-50', 'bg-violet-50', 'bg-amber-50', 'bg-rose-50'];
                          const projectTextColors = ['text-blue-800', 'text-emerald-800', 'text-violet-800', 'text-amber-800', 'text-rose-800'];
                          const bgColor = projectColors[gi % projectColors.length];
                          const textColor = projectTextColors[gi % projectTextColors.length];
                          return group.rows.map((row, ri) => (
                            <tr key={`${gi}-${ri}`} className={`${ri === 0 ? 'border-t-2 border-slate-300' : ''} ${bgColor} hover:brightness-95 transition-all`}>
                              {ri === 0 && (
                                <td rowSpan={group.rows.length} className={`px-4 py-2 border border-slate-200 font-bold ${textColor} align-top`}>
                                  <div className="text-sm font-bold">{group.project}</div>
                                  {group.subProjects && (
                                    <div className="mt-1 space-y-0.5">
                                      {group.subProjects.map((sp, si) => (
                                        <div key={si} className="text-xs text-slate-500 font-normal">{sp}</div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              )}
                              <td className="px-4 py-2 border border-slate-200 font-medium text-slate-700">{row.particular}</td>
                              <td className="px-4 py-2 border border-slate-200 text-right font-semibold text-slate-800">{row.req.toLocaleString()}</td>
                              <td className={`px-4 py-2 border border-slate-200 text-right font-bold ${row.done > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{row.done.toLocaleString()}</td>
                              <td className={`px-4 py-2 border border-slate-200 text-right font-semibold ${row.bal > 0 ? 'text-red-600' : row.bal === 0 ? 'text-emerald-600' : 'text-emerald-700'}`}>{row.bal.toLocaleString()}</td>
                            </tr>
                          ));
                        });
                      })()}
                      {/* TOTAL Row — only show when viewing all projects */}
                      {overviewProject === 'all' && mainIndexTotal && mainIndexTotal.rows.map((row, ri) => (
                        <tr key={`total-${ri}`} className={`${ri === 0 ? 'border-t-4 border-slate-900' : ''} bg-slate-800 text-white font-bold`}>
                          {ri === 0 && (
                            <td rowSpan={mainIndexTotal.rows.length} className="px-4 py-2 border border-slate-600 font-black text-lg align-middle">
                              TOTAL
                            </td>
                          )}
                          <td className="px-4 py-2 border border-slate-600 font-semibold">{row.particular}</td>
                          <td className="px-4 py-2 border border-slate-600 text-right font-bold">{row.req.toLocaleString()}</td>
                          <td className="px-4 py-2 border border-slate-600 text-right font-bold text-emerald-300">{row.done.toLocaleString()}</td>
                          <td className="px-4 py-2 border border-slate-600 text-right font-bold text-red-300">{row.bal.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage Wise Tab */}
        <TabsContent value="stage-wise">
          <StageWiseTab />
        </TabsContent>

        {/* Lead Entry Tab */}
        <TabsContent value="leads">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                <span>Lead Management ({totalLeads} leads)</span>
                <div className="flex gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input placeholder="Search..." value={searchInput} onChange={(e) => { 
                      setSearchInput(e.target.value);
                      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                      searchTimerRef.current = setTimeout(() => { setSearchTerm(e.target.value); setCurrentPage(1); }, 400);
                    }} className="pl-9 w-32" />
                  </div>
                  <Select value={filterGunthe} onValueChange={(v) => { setFilterGunthe(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-32" data-testid="lead-entry-filter-gunthe"><SelectValue placeholder="Gunthe" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Gunthe</SelectItem>
                      {(distinctGuntheOptions.length > 0 ? distinctGuntheOptions : LAND_SIZES).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterConfiguration} onValueChange={(v) => { setFilterConfiguration(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-36" data-testid="lead-entry-filter-configuration"><SelectValue placeholder="Configuration" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Configurations</SelectItem>
                      {(distinctConfigOptions.length > 0 ? distinctConfigOptions : CONFIGURATION_OPTIONS).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setFilterRemark('all'); setCurrentPage(1); }}>
                    <SelectTrigger className="w-28"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {STATUS_CATEGORIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterRemark} onValueChange={(v) => { setFilterRemark(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-36"><SelectValue placeholder="Remark" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Remarks</SelectItem>
                      {(filterStatus === 'all' ? REMARK_CATEGORIES : (STATUS_REMARK_MAPPING[filterStatus] || [])).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => setIsImportModalOpen(true)} data-testid="import-leads-btn">
                    <Upload className="w-4 h-4 mr-2" /> Import
                  </Button>
                  <Button variant="outline" onClick={handleExportExcel} disabled={exporting} data-testid="export-excel-btn">
                    {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Export
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openLeadModal()}>
                    <UserPlus className="w-4 h-4 mr-2" /> Add Lead
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-100">
                    <tr>
                      {[
                        { label: '#', field: null },
                        { label: 'ID', field: 'leadId' },
                        { label: 'Date', field: 'date' },
                        { label: 'Full Name', field: 'firstName' },
                        { label: 'Phone', field: 'phone' },
                        { label: 'Project', field: 'project' },
                        { label: 'DM Name / Agent Name', field: 'dmName' },
                        { label: 'Status', field: 'status' },
                        { label: 'Remark', field: 'remark' },
                        { label: 'Gunthe / Configuration', field: 'gunthe' },
                        { label: 'Flat / Shop No.', field: null },
                        { label: 'Actions', field: null },
                      ].map((col) => (
                        <th
                          key={col.label}
                          className={`px-2 py-2 text-left ${col.field ? 'cursor-pointer select-none hover:bg-stone-200 transition-colors' : ''}`}
                          onClick={col.field ? () => handleSort(col.field) : undefined}
                          data-testid={col.field ? `sort-${col.field}` : undefined}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {col.field && sortField === col.field && (
                              <span className="text-amber-700 font-bold">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                            )}
                            {col.field && sortField !== col.field && (
                              <span className="text-stone-300 text-[10px]">⇅</span>
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredLeads.map((lead, leadIdx) => {
                      const projInfo = ALL_PROJECTS_FOR_DROPDOWN.find(p => p.id === lead.project) || PROJECT_CATEGORIES.find(p => p.id === lead.project) || PROJECT_CATEGORIES[0];
                      const parentCategory = projInfo.parent 
                        ? PROJECT_CATEGORIES.find(p => p.id === projInfo.parent) 
                        : PROJECT_CATEGORIES.find(p => p.id === projInfo.id) || PROJECT_CATEGORIES[0];
                      return (
                      <tr key={lead.leadId} className="hover:bg-stone-50">
                        <td className="px-2 py-2 text-center text-[11px] font-semibold text-slate-400 select-none w-8">
                          {(currentPage - 1) * LEADS_PER_PAGE + leadIdx + 1}
                        </td>
                        <td className="px-2 py-2 font-mono text-xs" title={lead.leadId}>
                          {lead.seq ? `L-${String(lead.seq).padStart(7, '0')}` : lead.leadId}
                        </td>
                        <td className="px-2 py-2 text-xs">{lead.date}</td>
                        <td className="px-2 py-2">
                          <span className="font-medium">{lead.firstName}</span>
                          <span className="text-amber-600 font-bold mx-1">{lead.middleName}</span>
                          <span>{lead.lastName}</span>
                        </td>
                        <td className="px-2 py-2">{lead.phone}</td>
                        <td className="px-2 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${parentCategory?.bgLight || 'bg-blue-100'} ${parentCategory?.textColor || 'text-blue-800'}`} title={projInfo.name}>
                            {projInfo.name.replace(' (Siddhivinayak Infra)', '')}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-xs">{lead.dmName || lead.referenceSource || '-'}</td>
                        <td className="px-2 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[lead.status]?.bg || 'bg-gray-100'} ${STATUS_COLORS[lead.status]?.text || 'text-gray-700'}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-xs max-w-[120px] truncate" title={lead.remarkCategory || lead.remark}>
                          {lead.remarkCategory || lead.remark || '-'}
                        </td>
                        <td className="px-2 py-2">
                          {(lead.gunthe || lead.configuration) ? (
                            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">{lead.gunthe || lead.configuration}</span>
                          ) : '-'}
                        </td>
                        <td className="px-2 py-2 text-xs font-medium text-stone-700">{lead.flatNo || lead.shopNo || '-'}</td>
                        <td className="px-2 py-2">
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => openLeadModal(lead)} title="Edit Lead" data-testid={`edit-lead-${lead.id || lead.leadId}`}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-purple-600 hover:bg-purple-50" onClick={() => openConversationModal(lead)} title="Conversation History" data-testid={`conv-lead-${lead.id || lead.leadId}`}>
                              <MessageSquare className="w-3 h-3" />
                              {lead.conversations?.length > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-600 text-white text-[10px] rounded-full flex items-center justify-center">
                                  {lead.conversations.length}
                                </span>
                              )}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50 hover:text-red-700" onClick={async () => {
                              if (window.confirm(`Are you sure you want to remove this lead?\n${lead.name || 'Unknown'} - ${lead.mobileNumber || ''}`)) {
                                try {
                                  const res = await fetch(`${API}/marketing-dashboard/leads/${lead.id || lead.leadId}`, { method: 'DELETE' });
                                  if (res.ok) {
                                    toast.success('Lead removed!');
                                    await Promise.all([fetchLeads(), fetchStats(), fetchMainIndex()]);
                                    fetchOverviewStats(selectedProject);
                                    fetchProjectSummary();
                                    fetchYearMonthTrend(selectedTrendProject);
                                    setLeadsRefreshKey((k) => k + 1);
                                  }
                                } catch (err) { console.error('Delete failed:', err); }
                              }
                            }} title="Remove Lead" data-testid={`delete-lead-${lead.id || lead.leadId}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
                {totalLeads > LEADS_PER_PAGE && (
                  <div className="flex items-center justify-between mt-4 px-2">
                    <p className="text-sm text-stone-500">
                      Showing {((currentPage - 1) * LEADS_PER_PAGE) + 1} - {Math.min(currentPage * LEADS_PER_PAGE, totalLeads)} of {totalLeads} leads
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        data-testid="pagination-prev"
                      >
                        Previous
                      </Button>
                      {(() => {
                        const totalPages = Math.ceil(totalLeads / LEADS_PER_PAGE);
                        const pages = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                        let end = Math.min(totalPages, start + maxVisible - 1);
                        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
                        if (start > 1) pages.push(<Button key="first" variant="ghost" size="sm" onClick={() => setCurrentPage(1)}>1</Button>);
                        if (start > 2) pages.push(<span key="dots-start" className="text-stone-400">...</span>);
                        for (let i = start; i <= end; i++) {
                          pages.push(
                            <Button
                              key={i}
                              variant={i === currentPage ? 'default' : 'ghost'}
                              size="sm"
                              className={i === currentPage ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                              onClick={() => setCurrentPage(i)}
                            >
                              {i}
                            </Button>
                          );
                        }
                        if (end < totalPages - 1) pages.push(<span key="dots-end" className="text-stone-400">...</span>);
                        if (end < totalPages) pages.push(<Button key="last" variant="ghost" size="sm" onClick={() => setCurrentPage(totalPages)}>{totalPages}</Button>);
                        return pages;
                      })()}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage >= Math.ceil(totalLeads / LEADS_PER_PAGE)}
                        onClick={() => setCurrentPage(p => p + 1)}
                        data-testid="pagination-next"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Combined Remark Data & Bifurcation Tab */}
        <TabsContent value="remark-bifurcation">
          <div className="space-y-6">
            {/* Status × Remark Bifurcation Cards — Each block shows total + per-remark counts (auto-synced with global filter) */}
            {(() => {
              const ovStatusCounts = overviewStats.statusCounts || { INPROCESS: 0, HOLD: 0, CANCEL: 0, DONE: 0 };
              const ovRemarkCounts = overviewStats.remarkCounts || {};
              const blocks = [
                {
                  status: 'INPROCESS',
                  total: ovStatusCounts.INPROCESS || 0,
                  bgClass: 'bg-yellow-50 border-yellow-300',
                  headerClass: 'text-yellow-900 border-yellow-300',
                  rowClass: 'text-yellow-800',
                  countClass: 'text-yellow-900 bg-yellow-200',
                  remarks: STATUS_REMARK_MAPPING.INPROCESS,
                },
                {
                  status: 'HOLD',
                  total: ovStatusCounts.HOLD || 0,
                  bgClass: 'bg-blue-50 border-blue-300',
                  headerClass: 'text-blue-900 border-blue-300',
                  rowClass: 'text-blue-800',
                  countClass: 'text-blue-900 bg-blue-200',
                  remarks: STATUS_REMARK_MAPPING.HOLD,
                },
                {
                  status: 'CANCEL',
                  total: ovStatusCounts.CANCEL || 0,
                  bgClass: 'bg-red-50 border-red-300',
                  headerClass: 'text-red-900 border-red-300',
                  rowClass: 'text-red-800',
                  countClass: 'text-red-900 bg-red-200',
                  remarks: STATUS_REMARK_MAPPING.CANCEL,
                },
                {
                  status: 'DONE',
                  total: ovStatusCounts.DONE || 0,
                  bgClass: 'bg-green-50 border-green-300',
                  headerClass: 'text-green-900 border-green-300',
                  rowClass: 'text-green-800',
                  countClass: 'text-green-900 bg-green-200',
                  remarks: STATUS_REMARK_MAPPING.DONE,
                },
              ];
              return (
                <div className="space-y-4">
                  {/* Total Leads summary box */}
                  <div className="flex justify-center">
                    <div className="px-8 py-4 rounded-xl border-2 border-stone-800 bg-stone-800 text-white shadow-lg text-center min-w-[200px]">
                      <p className="text-xs font-bold uppercase tracking-widest text-stone-300 mb-1">Total Leads</p>
                      <p className="text-4xl font-extrabold tabular-nums">
                        {((ovStatusCounts.INPROCESS || 0) + (ovStatusCounts.HOLD || 0) + (ovStatusCounts.CANCEL || 0) + (ovStatusCounts.DONE || 0)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="remark-bifurcation-blocks">
                  {blocks.map((blk) => (
                    <div
                      key={blk.status}
                      className={`p-5 rounded-xl border-2 ${blk.bgClass} shadow-sm`}
                      data-testid={`remark-block-${blk.status.toLowerCase()}`}
                    >
                      <div className={`flex items-baseline justify-between pb-3 mb-3 border-b-2 ${blk.headerClass}`}>
                        <h3 className={`text-lg font-bold ${blk.headerClass.split(' ')[0]}`}>{blk.status}</h3>
                        <span className={`text-3xl font-bold ${blk.headerClass.split(' ')[0]} tabular-nums`}>
                          {blk.total.toLocaleString()}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {blk.remarks.map((remark) => {
                          const count = ovRemarkCounts[remark] || 0;
                          return (
                            <div
                              key={remark}
                              className="flex items-center justify-between gap-2"
                              data-testid={`remark-row-${remark.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}
                            >
                              <span className={`text-xs font-medium ${blk.rowClass} uppercase tracking-tight leading-tight`}>
                                {remark}
                              </span>
                              <span className={`text-xs font-bold ${blk.countClass} px-2 py-0.5 rounded-md tabular-nums min-w-[40px] text-center`}>
                                {count.toLocaleString()}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              );
            })()}

            {/* Lead-wise Remark Data Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Table className="w-5 h-5 text-blue-600" />
                  Lead-wise Remark Data (As per Master Excel)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border data-table">
                    <thead>
                      <tr className="bg-blue-600 text-white">
                        <th className="px-3 py-2 text-left border">PARTICULAR</th>
                        <th className="px-3 py-2 text-right border">TOTAL LEAD</th>
                        <th className="px-3 py-2 text-right border">TOTAL OFFICE VISIT</th>
                        <th className="px-3 py-2 text-right border">TOTAL PROJECT VISIT</th>
                        <th className="px-3 py-2 text-right border">TOTAL SCOPE OF CUSTOMER</th>
                        <th className="px-3 py-2 text-right border">TOTAL BOOKING</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Grand Total Row - Moved to Top */}
                      {remarkGrandTotal && (
                        <tr className="bg-blue-100 font-bold">
                          <td className="px-3 py-2 border text-blue-800">GRAND TOTAL</td>
                          <td className="px-3 py-2 border text-right text-blue-800">{remarkGrandTotal.totalLead?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-blue-800">{remarkGrandTotal.totalOfficeVisit?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-blue-800">{remarkGrandTotal.totalProjectVisit?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-blue-800">{remarkGrandTotal.totalScopeOfCustomer?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-emerald-800">{remarkGrandTotal.totalBooking?.toLocaleString()}</td>
                        </tr>
                      )}
                      {remarkData.length > 0 ? remarkData.map((row, idx) => (
                        <tr key={idx} className={getRemarkRowClass(row.particular)}>
                          <td className="px-3 py-2 border font-medium">{row.particular}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-700">{row.totalLead?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right">{row.totalOfficeVisit?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right">{row.totalProjectVisit?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right">{row.totalScopeOfCustomer?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-emerald-700">{row.totalBooking?.toLocaleString()}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">No lead data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Stage-wise Bifurcation Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-600" />
                  Stage-wise Bifurcation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border data-table">
                    <thead>
                      <tr className="bg-purple-600 text-white">
                        <th className="px-3 py-2 text-left border">PARTICULAR</th>
                        <th className="px-3 py-2 text-right border">Only Lead</th>
                        <th className="px-3 py-2 text-right border">Only Office Visit</th>
                        <th className="px-3 py-2 text-right border">Only Project Visit</th>
                        <th className="px-3 py-2 text-right border">Only Scope</th>
                        <th className="px-3 py-2 text-right border">Only Booking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Grand Total Row - Added at Top */}
                      {stageGrandTotal && (
                        <tr className="bg-purple-100 font-bold">
                          <td className="px-3 py-2 border text-purple-800">GRAND TOTAL</td>
                          <td className="px-3 py-2 border text-right text-purple-800">{stageGrandTotal.onlyLeadDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-purple-800">{stageGrandTotal.onlyOfficeVisitDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-purple-800">{stageGrandTotal.onlyProjectVisitDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-purple-800">{stageGrandTotal.onlyScopeDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-emerald-800">{stageGrandTotal.onlyBookingDone?.toLocaleString()}</td>
                        </tr>
                      )}
                      {stageBifurcation.length > 0 ? stageBifurcation.map((row, idx) => (
                        <tr key={idx} className={getRemarkRowClass(row.particular)}>
                          <td className="px-3 py-2 border font-medium">{row.particular}</td>
                          <td className="px-3 py-2 border text-right">{row.onlyLeadDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right">{row.onlyOfficeVisitDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right">{row.onlyProjectVisitDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right">{row.onlyScopeDone?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right text-emerald-700 font-bold">{row.onlyBookingDone?.toLocaleString()}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">No lead data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* DM Dashboard Tab - Driven by Global Project Filter */}
        <TabsContent value="dm-dashboard">
          <MarketingDMTab
            selectedProject={selectedProject}
            dmDashboardView={dmDashboardView}
            setDmDashboardView={setDmDashboardView}
            allDmData={allDmData}
            allDmGrandTotal={allDmGrandTotal}
            projectDmPerformance={projectDmPerformance}
            projectDmGrandTotal={projectDmGrandTotal}
            allGuntheData={allGuntheData}
            requirementWiseData={requirementWiseData}
            allAddressData={allAddressData}
            addressWiseData={addressWiseData}
            agentWiseData={agentWiseData}
            referenceWiseData={referenceWiseData}
            projectSummary={projectSummary}
            projectGrandTotal={projectGrandTotal}
            openQuickAddModal={openQuickAddModal}
          />
        </TabsContent>

        {/* Year/Month Trend Tab */}
        <TabsContent value="trend">
          <TrendTab
            yearMonthTrend={yearMonthTrend}
            selectedTrendProject={selectedTrendProject}
            trendYear={trendYear}
            setTrendYear={setTrendYear}
            expandedYears={expandedYears}
            setExpandedYears={setExpandedYears}
            expandedMonths={expandedMonths}
            setExpandedMonths={setExpandedMonths}
            monthDayData={monthDayData}
            setMonthDayData={setMonthDayData}
            API={API}
          />
        </TabsContent>

        {/* Team Hierarchy Tab */}
        <TabsContent value="hierarchy">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-purple-600" />
                Team Hierarchy: DM → ADM → CP → RCP
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {DM_TEAM_DATA.map((dm) => (
                  <div key={dm.id} className="border rounded-lg overflow-hidden">
                    <div 
                      className="flex items-center gap-3 p-4 bg-purple-50 cursor-pointer hover:bg-purple-100"
                      onClick={() => toggleDM(dm.id)}
                    >
                      {expandedDMs[dm.id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs">DM</div>
                      <div className="flex-1">
                        <p className="font-bold">{dm.name}</p>
                        <p className="text-xs text-stone-500">{dm.refId} | {dm.region}</p>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-blue-600">Leads: <b>{dm.leads.toLocaleString()}</b></span>
                        <span className="text-emerald-600">Bookings: <b>{dm.bookings}</b></span>
                      </div>
                    </div>
                    
                    {expandedDMs[dm.id] && dm.adms && dm.adms.length > 0 && (
                      <div className="ml-6 border-l-2 border-purple-200">
                        {dm.adms.map((adm) => (
                          <div key={adm.id}>
                            <div 
                              className="flex items-center gap-3 p-3 bg-indigo-50 border-b cursor-pointer hover:bg-indigo-100"
                              onClick={() => toggleADM(adm.id)}
                            >
                              {expandedADMs[adm.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-xs">ADM</div>
                              <div className="flex-1">
                                <p className="font-medium">{adm.name}</p>
                                <p className="text-xs text-stone-500">{adm.refId}</p>
                              </div>
                              <div className="flex gap-3 text-xs">
                                <span>Leads: <b>{adm.leads}</b></span>
                                <span className="text-emerald-600">Bookings: <b>{adm.bookings}</b></span>
                              </div>
                            </div>
                            
                            {expandedADMs[adm.id] && (
                              <div className="ml-6 border-l-2 border-indigo-200">
                                {CP_DATA.filter(cp => cp.admRef === adm.refId).map((cp) => (
                                  <div key={cp.id}>
                                    <div 
                                      className="flex items-center gap-3 p-3 bg-teal-50 border-b cursor-pointer hover:bg-teal-100"
                                      onClick={() => toggleCP(cp.id)}
                                    >
                                      {cp.rcps && cp.rcps.length > 0 ? (
                                        expandedCPs[cp.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                                      ) : <div className="w-4 h-4" />}
                                      <div className={`w-8 h-8 ${cp.type === 'SCP' ? 'bg-amber-500' : 'bg-teal-500'} rounded-full flex items-center justify-center text-white font-bold text-xs`}>
                                        {cp.type}
                                      </div>
                                      <div className="flex-1">
                                        <p className="font-medium">{cp.name}</p>
                                        <p className="text-xs text-stone-500">{cp.refId} | {cp.contact}</p>
                                      </div>
                                      <div className="flex gap-3 text-xs">
                                        <span>Leads: <b>{cp.leads}</b></span>
                                        <span className="text-emerald-600">Bookings: <b>{cp.bookings}</b></span>
                                      </div>
                                    </div>
                                    
                                    {expandedCPs[cp.id] && cp.rcps && cp.rcps.length > 0 && (
                                      <div className="ml-6 border-l-2 border-teal-200">
                                        {cp.rcps.map((rcp) => (
                                          <div key={rcp.id} className="flex items-center gap-3 p-2 bg-amber-50 border-b">
                                            <div className="w-4 h-4" />
                                            <div className="w-7 h-7 bg-orange-400 rounded-full flex items-center justify-center text-white font-bold text-[10px]">RCP</div>
                                            <div className="flex-1">
                                              <p className="font-medium text-sm">{rcp.name}</p>
                                              <p className="text-xs text-stone-500">{rcp.contact}</p>
                                            </div>
                                            <div className="flex gap-3 text-xs">
                                              <span>Leads: <b>{rcp.leads}</b></span>
                                              <span className="text-emerald-600">Bookings: <b>{rcp.bookings}</b></span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {CP_DATA.filter(cp => cp.admRef === adm.refId).length === 0 && (
                                  <div className="p-3 text-sm text-stone-500 italic">No CPs assigned</div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CP Structure Tab */}
        <TabsContent value="cp-structure">
          <CPStructureTab />
        </TabsContent>

        {/* DM Integration Tab */}
        <TabsContent value="dm-integration">
          <DMIntegrationTab />
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <ReportsTab API={API} handleExportExcel={handleExportExcel} exporting={exporting} leadsRefreshKey={leadsRefreshKey} globalProject={overviewProject} />
        </TabsContent>
      </Tabs>
      </div>

      {/* Lead Entry Modal */}
      <Dialog open={isLeadModalOpen} onOpenChange={setIsLeadModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLeadSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium">First Name *</label>
                <Input placeholder="First Name" value={leadForm.firstName} onChange={(e) => setLeadForm({...leadForm, firstName: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium text-amber-600">Middle Name * (Mandatory)</label>
                <Input placeholder="Middle Name" value={leadForm.middleName} onChange={(e) => setLeadForm({...leadForm, middleName: e.target.value})} required className="border-amber-400" />
              </div>
              <div>
                <label className="text-sm font-medium">Last Name *</label>
                <Input placeholder="Last Name" value={leadForm.lastName} onChange={(e) => setLeadForm({...leadForm, lastName: e.target.value})} required />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Phone *</label>
                <Input placeholder="Phone" value={leadForm.phone} onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input placeholder="Email" value={leadForm.email} onChange={(e) => setLeadForm({...leadForm, email: e.target.value})} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Address</label>
              <Input placeholder="Address" value={leadForm.address} onChange={(e) => setLeadForm({...leadForm, address: e.target.value})} />
            </div>

            {/* Project Selection - FIRST */}
            <div>
              <label className="text-sm font-medium text-blue-600">Project *</label>
              <Select value={leadForm.project || 'dream_kokan'} onValueChange={(v) => {
                const proj = ALL_PROJECTS_FOR_DROPDOWN.find(p => p.id === v);
                setLeadForm({
                  ...leadForm, 
                  project: v,
                  // Reset fields when switching project types
                  dmName: proj?.type === 'flat' ? '' : leadForm.dmName,
                  admName: proj?.type === 'flat' ? '' : leadForm.admName,
                  cpName: proj?.type === 'flat' ? '' : leadForm.cpName,
                  gunthe: proj?.type === 'flat' ? '' : leadForm.gunthe,
                  referenceSource: proj?.type === 'land' ? '' : leadForm.referenceSource,
                  referenceSourceManual: proj?.type === 'land' ? '' : leadForm.referenceSourceManual,
                  attendedBy: proj?.type === 'land' ? '' : leadForm.attendedBy,
                  configuration: proj?.type === 'land' ? '' : leadForm.configuration,
                  flatNo: proj?.type === 'land' ? '' : leadForm.flatNo,
                  shopNo: proj?.type === 'land' ? '' : leadForm.shopNo,
                });
              }}>
                <SelectTrigger className="border-blue-400"><SelectValue placeholder="Select Project" /></SelectTrigger>
                <SelectContent>
                  {ALL_PROJECTS_FOR_DROPDOWN.map(proj => (
                    <SelectItem key={proj.id} value={proj.id}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${proj.type === 'land' ? 'bg-blue-500' : 'bg-emerald-500'}`}></span>
                        {proj.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditional Fields for LAND Projects (Dream Kokan City, Dream Nagpur City) */}
            {(leadForm.project === 'dream_kokan' || leadForm.project === 'dream_nagpur') && (
              <>
                {/* DM Name Selection - Dropdown for Dream Kokan, Manual for Dream Nagpur */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm font-medium">DM Name</label>
                    {leadForm.project === 'dream_kokan' ? (
                      <Select value={leadForm.dmName} onValueChange={(v) => setLeadForm({...leadForm, dmName: v})}>
                        <SelectTrigger><SelectValue placeholder="Select DM" /></SelectTrigger>
                        <SelectContent>
                          {DM_TEAM_DATA.map(dm => <SelectItem key={dm.name} value={dm.name}>{dm.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        placeholder="Enter DM Name" 
                        value={leadForm.dmName} 
                        onChange={(e) => setLeadForm({...leadForm, dmName: e.target.value})} 
                      />
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium">ADM Name</label>
                    <Input placeholder="ADM Name" value={leadForm.admName} onChange={(e) => setLeadForm({...leadForm, admName: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">CP Name</label>
                    <Input placeholder="CP Name" value={leadForm.cpName} onChange={(e) => setLeadForm({...leadForm, cpName: e.target.value})} />
                  </div>
                </div>

                {/* Land Requirement & Deal Type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-teal-600">Land Requirement (Gunthe) *</label>
                    <Select value={leadForm.gunthe} onValueChange={(v) => setLeadForm({...leadForm, gunthe: v})}>
                      <SelectTrigger className="border-teal-400"><SelectValue placeholder="Select Gunthe" /></SelectTrigger>
                      <SelectContent>
                        {LAND_SIZES.map(size => <SelectItem key={size} value={size}>{size}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Deal Type *</label>
                    <Select value={leadForm.dealType} onValueChange={(v) => setLeadForm({...leadForm, dealType: v})}>
                      <SelectTrigger><SelectValue placeholder="Select Deal Type" /></SelectTrigger>
                      <SelectContent>
                        {DEAL_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Phase Type - Only for Dream Nagpur City */}
                {leadForm.project === 'dream_nagpur' && (
                  <div>
                    <label className="text-sm font-medium text-orange-600">Phase Type *</label>
                    <Select value={leadForm.phaseType} onValueChange={(v) => setLeadForm({...leadForm, phaseType: v})}>
                      <SelectTrigger className="border-orange-400"><SelectValue placeholder="Select Phase" /></SelectTrigger>
                      <SelectContent>
                        {NAGPUR_PHASE_TYPES.map(phase => <SelectItem key={phase} value={phase}>{phase}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* Conditional Fields for FLAT Projects (Siddhivinayak Infra, Dream Riviera, Sai Plaza) */}
            {leadForm.project !== 'dream_kokan' && leadForm.project !== 'dream_nagpur' && (
              <>
                {/* Reference Source */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-purple-600">Reference Source *</label>
                    <Select value={leadForm.referenceSource} onValueChange={(v) => setLeadForm({...leadForm, referenceSource: v, referenceSourceManual: ''})}>
                      <SelectTrigger className="border-purple-400"><SelectValue placeholder="Select Reference Source" /></SelectTrigger>
                      <SelectContent>
                        {REFERENCE_SOURCES.map(src => <SelectItem key={src} value={src}>{src}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-purple-600">Source Details (Manual)</label>
                    <Input 
                      placeholder={leadForm.referenceSource ? `Enter ${leadForm.referenceSource} details` : "Select source first"} 
                      value={leadForm.referenceSourceManual} 
                      onChange={(e) => setLeadForm({...leadForm, referenceSourceManual: e.target.value})} 
                      disabled={!leadForm.referenceSource}
                      className="border-purple-400"
                    />
                  </div>
                </div>

                {/* Attended By */}
                <div>
                  <label className="text-sm font-medium">Attended By</label>
                  <Input 
                    placeholder="Enter name of person who attended" 
                    value={leadForm.attendedBy} 
                    onChange={(e) => setLeadForm({...leadForm, attendedBy: e.target.value})} 
                  />
                </div>

                {/* Configuration */}
                <div>
                  <label className="text-sm font-medium text-teal-600">Configuration *</label>
                  <Select value={leadForm.configuration} onValueChange={(v) => setLeadForm({...leadForm, configuration: v})}>
                    <SelectTrigger className="border-teal-400"><SelectValue placeholder="Select Configuration" /></SelectTrigger>
                    <SelectContent>
                      {CONFIGURATION_OPTIONS.map(config => <SelectItem key={config} value={config}>{config}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Status & Remark Category */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Status *</label>
                <Select value={leadForm.status || 'INPROCESS'} onValueChange={(v) => setLeadForm({...leadForm, status: v, remarkCategory: ''})}>
                  <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                  <SelectContent>
                    {STATUS_CATEGORIES.map(s => (
                      <SelectItem key={s} value={s}>
                        <span className={`px-2 py-0.5 rounded ${STATUS_COLORS[s]?.bg} ${STATUS_COLORS[s]?.text}`}>{s}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-purple-600">Remark Category *</label>
                <Select value={leadForm.remarkCategory} onValueChange={(v) => setLeadForm({...leadForm, remarkCategory: v})}>
                  <SelectTrigger className="border-purple-400"><SelectValue placeholder="Select Remark" /></SelectTrigger>
                  <SelectContent>
                    {(STATUS_REMARK_MAPPING[leadForm.status || 'INPROCESS'] || REMARK_CATEGORIES).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Stage & Booking Date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Stage *</label>
                <Select value={leadForm.stage} onValueChange={(v) => setLeadForm({...leadForm, stage: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Stage" /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Booking Date</label>
                <Input type="date" value={leadForm.bookingDate} onChange={(e) => setLeadForm({...leadForm, bookingDate: e.target.value})} />
              </div>
            </div>

            {leadForm.project !== 'dream_kokan' && leadForm.project !== 'dream_nagpur' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-cyan-700">Flat No.</label>
                  <Input data-testid="marketing-lead-flat-no" placeholder="Enter Flat No." value={leadForm.flatNo} onChange={(e) => setLeadForm({...leadForm, flatNo: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium text-cyan-700">Shop No.</label>
                  <Input data-testid="marketing-lead-shop-no" placeholder="Enter Shop No." value={leadForm.shopNo} onChange={(e) => setLeadForm({...leadForm, shopNo: e.target.value})} />
                </div>
              </div>
            )}

            {/* RCP Name & Employee Name (who added lead) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-cyan-600">RCP Name</label>
                <Input 
                  placeholder="Enter RCP Name" 
                  value={leadForm.rcpName} 
                  onChange={(e) => setLeadForm({...leadForm, rcpName: e.target.value})}
                  className="border-cyan-400"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-indigo-600">Added By (Employee)</label>
                <Input 
                  placeholder="Employee Name" 
                  value={leadForm.addedByEmployee} 
                  onChange={(e) => setLeadForm({...leadForm, addedByEmployee: e.target.value})}
                  className="border-indigo-400"
                  disabled={editingLead !== null}
                />
                {editingLead && leadForm.addedByEmployee && (
                  <p className="text-xs text-indigo-500 mt-1">Lead added by: {leadForm.addedByEmployee}</p>
                )}
              </div>
            </div>

            {/* Remark/Comment */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Additional Remark
              </label>
              <Textarea placeholder="Add remarks..." value={leadForm.remark} onChange={(e) => setLeadForm({...leadForm, remark: e.target.value})} rows={2} />
            </div>

            {/* Conversation Section (Only show when editing) */}
            {editingLead && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium flex items-center gap-2 text-purple-700">
                    <History className="w-4 h-4" /> Add Conversation Remark
                  </label>
                  {editingLead.conversations?.length > 0 && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openConversationModal(editingLead)}
                      className="text-purple-600"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" /> View History ({editingLead.conversations.length})
                    </Button>
                  )}
                </div>
                
                {/* Last Conversation Display */}
                {editingLead.lastConversation && (
                  <div className="bg-purple-50 p-3 rounded-lg mb-3 text-sm border border-purple-200">
                    <div className="flex justify-between">
                      <span className="font-medium text-purple-800">Last Conversation:</span>
                      <span className="text-xs text-purple-600">{editingLead.lastConversation.employeeName} - {editingLead.lastConversation.timestamp ? new Date(editingLead.lastConversation.timestamp).toLocaleDateString('en-IN') : '-'}</span>
                    </div>
                    <p className="mt-1 text-stone-700">{editingLead.lastConversation.remark}</p>
                  </div>
                )}

                <div className="bg-stone-50 p-3 rounded-lg border">
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <div>
                      <label className="text-xs font-medium">Employee Name *</label>
                      <Input 
                        value={leadForm.conversationEmployeeName} 
                        onChange={(e) => setLeadForm({...leadForm, conversationEmployeeName: e.target.value})}
                        placeholder="Your Name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium">INPROCESS Category</label>
                      <Select value={leadForm.inprocessCategory} onValueChange={(v) => setLeadForm({...leadForm, inprocessCategory: v})}>
                        <SelectTrigger><SelectValue placeholder="Select Category" /></SelectTrigger>
                        <SelectContent>
                          {INPROCESS_CATEGORIES.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium">New Conversation Remark</label>
                    <Textarea 
                      value={leadForm.newConversation} 
                      onChange={(e) => setLeadForm({...leadForm, newConversation: e.target.value})}
                      placeholder="Enter conversation details, customer feedback, next actions..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                {submitting ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editingLead ? 'Update Lead' : 'Create Lead'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsLeadModalOpen(false)} disabled={submitting}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              Import Leads from Excel/CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 mb-2"><strong>Step 1:</strong> Download template or upload your file</p>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" /> Download Template
              </Button>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium"><strong>Step 2:</strong> Select project for these leads</label>
              <select
                value={importProject}
                onChange={(e) => setImportProject(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="import-project-select"
              >
                <option value="">-- Select Project (Required) --</option>
                <option value="dream_kokan">Dream Kokan City</option>
                <option value="dream_nagpur">Dream Nagpur City</option>
                <option value="brahma_heights">Brahma Heights</option>
                <option value="godavari_heights">Godavari Heights</option>
                <option value="siddhivinayak_heights">Siddhivinayak Heights</option>
                <option value="kaveri_heights">Kaveri Heights</option>
                <option value="dream_riviera_a">Dream Riviera 'A' Wing</option>
                <option value="dream_riviera_b">Dream Riviera 'B' Wing</option>
                <option value="sai_plaza">Sai Plaza</option>
              </select>
              {!importProject && <p className="text-xs text-amber-600">Please select a project so all leads are tagged correctly</p>}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium"><strong>Step 3:</strong> Select your Excel/CSV file</label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} ref={fileInputRef} />
              {importFile && (
                <p className="text-sm text-stone-600">Selected: <strong>{importFile.name}</strong> ({(importFile.size / 1024).toFixed(1)} KB)</p>
              )}
            </div>

            {importPreview && importPreview.success && (
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-800">
                  <strong>Preview:</strong> {importPreview.totalRows} rows found
                  <span className="ml-2 px-2 py-0.5 bg-emerald-200 rounded text-xs">{importPreview.validRows} valid</span>
                  {importPreview.invalidRows > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-red-200 rounded text-xs">{importPreview.invalidRows} invalid</span>
                  )}
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button onClick={handleImport} disabled={!importFile || !importProject || importing} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {importing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importing...</> : 'Import Leads'}
              </Button>
              <Button onClick={handleHistoricalImport} disabled={!importFile || !importProject || importing} variant="outline" className="flex-1">
                {importing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /></> : 'Import Historical Data'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add Lead Modal */}
      <Dialog open={isQuickAddOpen} onOpenChange={setIsQuickAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quickAddCategory && React.createElement(quickAddCategory.icon, { className: `w-5 h-5 ${quickAddCategory.textColor}` })}
              Quick Add Lead - {quickAddCategory?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickAddSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium">First Name *</label>
                <Input 
                  value={quickAddForm.firstName} 
                  onChange={(e) => setQuickAddForm({...quickAddForm, firstName: e.target.value})}
                  placeholder="First" required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-amber-600">Middle Name *</label>
                <Input 
                  value={quickAddForm.middleName} 
                  onChange={(e) => setQuickAddForm({...quickAddForm, middleName: e.target.value})}
                  placeholder="Middle" required className="border-amber-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium">Last Name *</label>
                <Input 
                  value={quickAddForm.lastName} 
                  onChange={(e) => setQuickAddForm({...quickAddForm, lastName: e.target.value})}
                  placeholder="Last" required
                />
              </div>
            </div>

            {/* Phone & Gunthe */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Phone *</label>
                <Input 
                  value={quickAddForm.phone} 
                  onChange={(e) => setQuickAddForm({...quickAddForm, phone: e.target.value})}
                  placeholder="Phone Number" required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-teal-600">Land Requirement *</label>
                <Select value={quickAddForm.gunthe} onValueChange={(v) => setQuickAddForm({...quickAddForm, gunthe: v})}>
                  <SelectTrigger className="border-teal-400"><SelectValue placeholder="Select Gunthe" /></SelectTrigger>
                  <SelectContent>
                    {LAND_SIZES.map(size => <SelectItem key={size} value={size}>{size}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* DM Name */}
            <div>
              <label className="text-xs font-medium">DM Name</label>
              <Select value={quickAddForm.dmName} onValueChange={(v) => setQuickAddForm({...quickAddForm, dmName: v})}>
                <SelectTrigger><SelectValue placeholder="Select DM" /></SelectTrigger>
                <SelectContent>
                  {DM_TEAM_DATA.map(dm => <SelectItem key={dm.id} value={dm.name}>{dm.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Initial Conversation */}
            <div className="p-3 rounded-lg bg-stone-50 border">
              <label className="text-xs font-medium flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4" /> Initial Conversation Remark
              </label>
              <Textarea 
                value={quickAddForm.initialConversation} 
                onChange={(e) => setQuickAddForm({...quickAddForm, initialConversation: e.target.value})}
                placeholder="Enter conversation details..."
                rows={2}
              />
              <div className="mt-2">
                <label className="text-xs text-stone-500">Your Name (Employee)</label>
                <Input 
                  value={quickAddForm.employeeName} 
                  onChange={(e) => setQuickAddForm({...quickAddForm, employeeName: e.target.value})}
                  placeholder="Employee Name"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIsQuickAddOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className={`flex-1 ${quickAddCategory?.color} text-white`}>
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Lead
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Conversation History Modal */}
      <Dialog open={isConversationModalOpen} onOpenChange={setIsConversationModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Conversation History - {conversationLead?.firstName} {conversationLead?.middleName} {conversationLead?.lastName}
            </DialogTitle>
          </DialogHeader>
          
          {/* Lead Info */}
          <div className="p-3 bg-stone-50 rounded-lg mb-4 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div><span className="text-stone-500">Lead ID:</span> <span className="font-medium">{conversationLead?.leadId}</span></div>
              <div><span className="text-stone-500">Phone:</span> <span className="font-medium">{conversationLead?.phone}</span></div>
              <div><span className="text-stone-500">Status:</span> <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[conversationLead?.status]?.bg || 'bg-gray-100'} ${STATUS_COLORS[conversationLead?.status]?.text || 'text-gray-700'}`}>{conversationLead?.status}</span></div>
            </div>
          </div>

          {/* Conversation List */}
          <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No conversations recorded yet</p>
              </div>
            ) : (
              conversations.map((conv, idx) => (
                <div key={conv.id || idx} className="p-3 border rounded-lg bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm">{conv.remark}</p>
                      {conv.category && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                          {INPROCESS_CATEGORIES.find(c => c.id === conv.category)?.name || conv.category}
                        </span>
                      )}
                    </div>
                    <div className="text-right text-xs text-stone-500">
                      <p className="font-medium text-stone-700">{conv.employeeName}</p>
                      <p>{conv.timestamp ? new Date(conv.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add New Conversation */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add New Conversation
            </h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium">Employee Name *</label>
                  <Input 
                    value={newConversation.employeeName} 
                    onChange={(e) => setNewConversation({...newConversation, employeeName: e.target.value})}
                    placeholder="Your Name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Category</label>
                  <Select value={newConversation.category} onValueChange={(v) => setNewConversation({...newConversation, category: v})}>
                    <SelectTrigger><SelectValue placeholder="Select Category" /></SelectTrigger>
                    <SelectContent>
                      {INPROCESS_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium">Remark / Conversation Details *</label>
                <Textarea 
                  value={newConversation.remark} 
                  onChange={(e) => setNewConversation({...newConversation, remark: e.target.value})}
                  placeholder="Enter conversation details, customer feedback, next steps..."
                  rows={3}
                />
              </div>
              <Button onClick={handleAddConversation} className="w-full bg-purple-600 hover:bg-purple-700">
                <MessageSquare className="w-4 h-4 mr-2" /> Add Conversation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== REPORTS TAB COMPONENT ====================
export default MarketingDashboard;
